import React, { useContext, useEffect, useState, useRef } from 'react';
import { AudioContext } from '../contexts/AudioContext';
import axiosInstance from '../index';
import './VerseText.css';

function VerseText({ onUnauthorized }) {
  const { currentChapter, currentTime, duration } = useContext(AudioContext);
  const [verses, setVerses] = useState({});
  const [currentVerse, setCurrentVerse] = useState(0);
  const [cumulativeWordCounts, setCumulativeWordCounts] = useState([]);
  const [totalWords, setTotalWords] = useState(0);
  const [manualOffset, setManualOffset] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const versesRef = useRef(null);
  const containerRef = useRef(null);
  const firstVerseRef = useRef(null);
  const lastVerseRef = useRef(null);

  // Calculate adjusted progress at the top level
  const getAdjustedProgress = () => {
    if (!duration || !currentTime || !currentChapter) return 0;
    const startDelay = currentChapter.chapter === 1 ? 3.5 : 3;
    const endBuffer = 3;
    const adjustedTime = Math.max(0, currentTime - startDelay);
    // console.log(`Current Time: ${currentTime}, Adjusted Time: ${adjustedTime}`);
    const adjustedDuration = Math.max(0, duration - startDelay - endBuffer);
    const progress = adjustedDuration > 0 ? adjustedTime / adjustedDuration : 0;
    return Math.min(1, Math.max(0, progress));
  };

  // Calculate offset using the adjusted progress
  const offset = duration > 0 ? getAdjustedProgress() * 100 : 0;
  const finalOffset = Math.min(100, Math.max(0, offset + manualOffset));

  // Fetch verses effect
  useEffect(() => {
    if (currentChapter) {
      // Set transitioning state to true when the chapter changes
      // This will temporarily disable animations to allow instant position change
      setIsTransitioning(true);
      
      const fetchVerses = async () => {
        try {
          const response = await axiosInstance.get(`/static/data/esv/${currentChapter.book}.json`);
          const chapterVerses = response.data[currentChapter.book][currentChapter.chapter];
          setVerses(chapterVerses);

          // Calculate cumulative word counts and total words
          const cumulativeCounts = [];
          let words = 0;
          Object.keys(chapterVerses).forEach(verse => {
            const wordCount = chapterVerses[verse].split(' ').length;
            words += wordCount;
            cumulativeCounts.push(words);
          });
          setCumulativeWordCounts(cumulativeCounts);
          setTotalWords(words);
          setManualOffset(0); // Reset manual offset when chapter changes
          
          // After a brief delay, re-enable animations
          // This delay ensures DOM has updated with new verses and calculations
          setTimeout(() => {
            setIsTransitioning(false);
          }, 300);
        } catch (error) {
          console.error('Error fetching verses:', error);
          setIsTransitioning(false);
          if (error.response?.status === 401) {
            onUnauthorized?.();
          }
        }
      };
      fetchVerses();
    }
  }, [currentChapter]);

  // Update current verse effect
  useEffect(() => {
    if (currentChapter && Object.keys(verses).length > 0 && duration > 0) {
      // Use the same progress calculation
      const progress = getAdjustedProgress();
      const targetWord = Math.floor(progress * totalWords);
      const newCurrentVerse = cumulativeWordCounts.findIndex(count => count >= targetWord);
      // console.log(`Target Word: ${targetWord}, New Current Verse: ${newCurrentVerse}`);
      setCurrentVerse(Math.max(0, newCurrentVerse)); // Ensure we never get -1
    }
  }, [currentChapter, verses, currentTime, duration, cumulativeWordCounts, totalWords]);

  
  const calculateTransform = () => {
    if (!containerRef.current || !firstVerseRef.current || !lastVerseRef.current) {
      return 0;
    }

    const containerHeight = containerRef.current.clientHeight;
    const firstVerseHeight = firstVerseRef.current.clientHeight;
    const lastVerseHeight = lastVerseRef.current.clientHeight;

    // Calculate start and end positions
    const startY = containerHeight/2 - firstVerseHeight/2;
    const endY = -(lastVerseRef.current.offsetTop) + containerHeight/2 - lastVerseHeight/2;
    
    // Interpolate between start and end positions based on playback progress
    const progress = (finalOffset / 100);
    return Math.round(startY + (endY - startY) * progress);
  };

  return (
    <div className="verse-text">
      {currentChapter ? (
        <div className="verse-container" ref={containerRef}>
          <div className="fade-overlay" />
          <div 
            className={`verses ${isTransitioning ? 'transitioning' : ''}`}
            ref={versesRef}
            style={{ transform: `translateY(${calculateTransform()}px)` }}
          >
            {Object.keys(verses).map((verse, index, array) => {
              const verseNum = Number(verse);
              const isCurrent = verseNum === currentVerse + 1;
              const isVeryNear = Math.abs(verseNum - (currentVerse + 1)) <= 1;
              const isNear = Math.abs(verseNum - (currentVerse + 1)) <= 2;
              const isFirst = index === 0;
              const isLast = index === array.length - 1;
              
              return (
                <p 
                  key={verse} 
                  ref={isFirst ? firstVerseRef : isLast ? lastVerseRef : null}
                  className={`
                    ${isCurrent ? 'current-verse' : ''}
                    ${!isCurrent && isVeryNear ? 'very-near-current' : ''}
                    ${!isCurrent && !isVeryNear && isNear ? 'near-current' : ''}
                  `.trim()}
                >
                  {verse}: {verses[verse]}
                </p>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="verse-container empty-state">
          <div className="empty-message">Select a chapter to begin</div>
        </div>
      )}
    </div>
  );
}

export default VerseText;